import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { HandOffPartner } from "../../app/javascript/components/__generated__/graphql";
import { RootState } from "./reduxStore";
import { requireRef } from "../../app/javascript/components/hand_off_partner_portal/components/util";

interface PartnerPortalState {
  currentHandOffPartnerRef: string | null;
  linkedHandOffPartnersCache: Record<string, HandOffPartner>;
  userHandOffPartners: HandOffPartner[];
}

const initialState: () => PartnerPortalState = () => {
  // The selection used to be stored by id; drop it so the user's own partner is reselected.
  localStorage.removeItem("currentHandOffPartnerId");
  return {
    currentHandOffPartnerRef: localStorage.getItem("currentHandOffPartnerRef"),
    linkedHandOffPartnersCache: {},
    userHandOffPartners: [],
  };
};

const partnerPortalSlice = createSlice({
  name: "partnerPortal",
  initialState: initialState(),
  reducers: {
    setCurrentHandOffPartnerRef: (
      state: PartnerPortalState,
      action: PayloadAction<string | null>,
    ) => {
      state.currentHandOffPartnerRef = action.payload;
      if (action.payload !== null) {
        localStorage.setItem("currentHandOffPartnerRef", action.payload);
      } else {
        localStorage.removeItem("currentHandOffPartnerRef");
      }
    },
    setLinkedHandOffPartnerInCache: (
      state: PartnerPortalState,
      action: PayloadAction<HandOffPartner>,
    ) => {
      const partner = action.payload;
      state.linkedHandOffPartnersCache[requireRef(partner.ref)] = partner;
    },
    setLinkedHandOffPartnersInCache: (
      state: PartnerPortalState,
      action: PayloadAction<HandOffPartner[]>,
    ) => {
      const partners = action.payload;
      partners.forEach((partner) => {
        state.linkedHandOffPartnersCache[requireRef(partner.ref)] = partner;
      });
    },
    setUserHandOffPartners: (
      state: PartnerPortalState,
      action: PayloadAction<HandOffPartner[]>,
    ) => {
      state.userHandOffPartners = action.payload;
    },
  },
});

export const {
  setCurrentHandOffPartnerRef,
  setLinkedHandOffPartnerInCache,
  setLinkedHandOffPartnersInCache,
  setUserHandOffPartners,
} = partnerPortalSlice.actions;

export const selectPartnerInfo = createSelector(
  [
    (state: RootState) => state.partnerPortal.currentHandOffPartnerRef,
    (state: RootState) => state.partnerPortal.userHandOffPartners,
    (state: RootState) => state.partnerPortal.linkedHandOffPartnersCache,
  ],
  (currentPartnerRef, userPartners, linkedPartnersCache) => {
    // First check userHandOffPartners (partners user is directly assigned to)
    const fromUserPartners = userPartners.find(
      (partner) => partner.ref === currentPartnerRef,
    );
    if (fromUserPartners) {
      return fromUserPartners;
    }

    // Then check linkedHandOffPartnersCache (for super user accessible partners)
    return (
      (currentPartnerRef && linkedPartnersCache[currentPartnerRef]) || null
    );
  },
);

export default partnerPortalSlice.reducer;
